"use client";

import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

export default function Tabs({
  activeTab,
  setActiveTab,
  tabs, // Array of { id, label, icon, badge }
}) {
  const tabPanelRef = useRef(null);
  const [noisePhase, setNoisePhase] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [direction, setDirection] = useState(0);

  // Smooth liquid animation
  useEffect(() => {
    if (!isAnimating) return;

    let lastTime = 0;
    const interval = 1000 / 30; // 30fps for smooth animation

    const animate = (time) => {
      if (time - lastTime > interval) {
        setNoisePhase((prev) => (prev + 0.001) % 1); // Very subtle movement
        lastTime = time;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    const animationRef = { current: requestAnimationFrame(animate) };

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAnimating]);

  const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTab);

  // Swipe handling
  const handleDragEnd = (event, info) => {
    if (info.offset.x < -50) {
      if (activeTabIndex < tabs.length - 1) {
        setDirection(1);
        setActiveTab(tabs[activeTabIndex + 1].id);
      }
    } else if (info.offset.x > 50) {
      if (activeTabIndex > 0) {
        setDirection(-1);
        setActiveTab(tabs[activeTabIndex - 1].id);
      }
    }
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? -100 : 100,
      opacity: 0,
    }),
  };

  return (
    <div
      className="px-4 tab-panel relative"
      id="tab-panel"
      ref={tabPanelRef}
    >
      {/* SVG Filters for Liquid Glass Effect */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter
            id="liquid-glass"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
          >
            <feTurbulence
              type="fractalNoise"
              numOctaves="2"
              seed="2"
              result="turbulence"
              baseFrequency={`${0.008} ${0.008 + noisePhase * 0.003}`}
            />
            <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="2" />
            <feGaussianBlur stdDeviation="0.2" />
          </filter>
        </defs>
      </svg>

      {/* Main Tab Container */}
      <motion.div
        className="relative overflow-hidden rounded-2xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Background Glass Layer */}
        <div className="absolute inset-0 bg-white/50 dark:bg-gradient-to-br dark:from-white/[0.15] dark:via-white/[0.08] dark:to-white/[0.05] backdrop-blur-xl">
          {/* Subtle liquid overlay */}
          <div
            className="absolute inset-0 bg-gradient-to-br from-blue-400/5 to-purple-400/5 dark:from-blue-400/3 dark:to-purple-400/3 opacity-60"
            style={{ filter: isAnimating ? "url(#liquid-glass)" : "none" }}
          />
        </div>

        {/* Border Highlight */}
        <div className="absolute inset-0 rounded-2xl ring-1 ring-slate-200 dark:ring-white/20 ring-inset" />

        {/* Tab Buttons Container */}
        <motion.div
          className="relative flex p-2 gap-1"
          drag="x"
          dragConstraints={tabPanelRef}
          dragElastic={0.1}
          onDragEnd={handleDragEnd}
        >
          {/* Layout ID based Sliding Tab will be directly inside the buttons */}

          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              id={tab.id}
              className={`relative flex-1 py-3 px-4 rounded-xl font-semibold text-center cursor-pointer transition-all duration-200 ease-out z-10 flex items-center justify-center gap-2 ${
                activeTab === tab.id
                  ? "text-slate-900 dark:text-white transform scale-[1.02]"
                  : "text-slate-600 dark:text-white/75 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="active-tab-indicator"
                  className="absolute inset-0 bg-white dark:bg-slate-600 rounded-xl shadow-sm dark:shadow-lg ring-1 ring-slate-200 dark:ring-white/10 ring-inset"
                  initial={false}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0C8BC4]/10 via-[#9333EA]/5 to-transparent rounded-xl" />
                </motion.div>
              )}
              {tab.icon && <span className="relative z-10">{tab.icon}</span>}
              <span className="relative z-10">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full px-2 py-0.5 shadow-lg ring-2 ring-white/20">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </motion.div>

        {/* Bottom highlight line */}
        <div className="absolute bottom-0 left-2 right-2 h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-white/20 to-transparent" />

        {/* Optional: Toggle button for liquid effect */}
        <button
          onClick={() => setIsAnimating(!isAnimating)}
          className="absolute bottom-1 right-1 text-[10px] text-slate-400 dark:text-white/30 hover:text-slate-600 dark:hover:text-white/50 transition-colors p-1 opacity-50 hover:opacity-100"
          title={isAnimating ? "Disable liquid effect" : "Enable liquid effect"}
        >
          {isAnimating ? "◉" : "○"}
        </button>
      </motion.div>
    </div>
  );
}
